import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastService } from '../../ui';
import { FixRegulationDialogComponent, FixRegulationDialogData } from './fix-regulation-dialog.component';
import { copyToClipboard } from '../../shared/utils/clipboard';
import { CcNumbersService } from './cc-numbers.service';
import {
  PhoneNumber,
  RegulationAddressDetails,
  shortNotation,
  VOXBONE_ERROR_STATUSES,
  VOXBONE_PENDING_STATUSES,
} from './cc-numbers.model';
import {
  HLM_DIALOG_SHELL,
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmTooltipDirective,
} from '../../ui';

export interface NumberDetailsDialogData {
  number: PhoneNumber;
  phoneServiceId: number;
}

/**
 * Read-only "Number Details" dialog: internal technical info for a single number
 * (short notation, number block, carrier/routing, regulation). Opened from the discreet ⓘ
 * icon on the CC Numbers list — hidden from the 99%, on demand for techies.
 *
 * All sections render immediately from the already-loaded PhoneNumber except the regulation
 * address, which is lazy-loaded and only available for failed/rejected numbers (PS-API constraint).
 */
@Component({
  selector: 'cmt-number-details-dialog',
  standalone: true,
  imports: [
    ...HLM_DIALOG_SHELL,
    HlmBadgeComponent, HlmBtnDirective, HlmIconComponent, HlmTooltipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <hlm-dialog-surface size="lg">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge [tone]="isRegulationError() ? 'destructive' : 'primary'">
          <hlm-icon [name]="isRegulationError() ? 'lucideCircleX' : 'lucideInfo'" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle class="font-mono-feat">{{ n.number }}</h2>
          <p hlmDialogDescription>
            <hlm-badge [variant]="n.type === 'HIDDEN' ? 'secondary' : n.type === 'SERVICE' ? 'accent' : 'default'">
              {{ n.type }}
            </hlm-badge>
          </p>
        </div>
        <button hlmDialogClose tabindex="-1" (click)="close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody class="@container flex flex-col gap-5">
        <!-- Short Notation (highlighted) -->
        <div class="rounded-lg border border-border bg-muted/40 p-3">
          <div class="flex items-center justify-between gap-3">
            <span class="cmt-eyebrow">
              Short Notation
            </span>
            <div class="flex items-center gap-2">
              <span class="font-mono-feat text-base font-semibold text-foreground">{{ short() }}</span>
              <button hlmBtn variant="ghost" size="icon" class="h-7 w-7"
                (click)="copy(short(), $event)" hlmTooltip="Copy short notation">
                <hlm-icon name="lucideCopy" size="0.8rem" />
              </button>
            </div>
          </div>
        </div>

        <!-- Number + Number Block reflow side-by-side once the dialog is wide enough
             (container query on the body) — avoids a tall vertical scroll when horizontal room exists. -->
        <div class="grid grid-cols-1 gap-5 @xl:grid-cols-2">
          <!-- ===== NUMBER ===== -->
          <div class="flex flex-col gap-1">
            <p class="cmt-eyebrow">Number</p>
            <dl class="flex flex-col divide-y divide-border/70">
              <div class="flex items-center justify-between gap-4 py-1.5">
                <dt class="text-base text-muted-foreground">Full number</dt>
                <dd class="font-mono-feat text-base text-foreground">{{ n.number }}</dd>
              </div>
              <div class="flex items-center justify-between gap-4 py-1.5">
                <dt class="whitespace-nowrap text-base text-muted-foreground" hlmTooltip="Dialling code / Prefix / Suffix">Split</dt>
                <dd class="whitespace-nowrap font-mono-feat text-base text-foreground">{{ dash(n.numberBlock?.vorwahl) }} / {{ dash(n.numberBlock?.prefix) }} / {{ dash(n.suffix) }}</dd>
              </div>
              <div class="flex items-center justify-between gap-4 py-1.5">
                <dt class="text-base text-muted-foreground">Type</dt>
                <dd class="text-base text-foreground">{{ n.type }}</dd>
              </div>
              <div class="flex items-center justify-between gap-4 py-1.5">
                <dt class="text-base text-muted-foreground">Number ID</dt>
                <dd class="font-mono-feat text-base text-foreground">#{{ n.id }}</dd>
              </div>
              <div class="flex items-center justify-between gap-4 py-1.5">
                <dt class="text-base text-muted-foreground">Provider type</dt>
                <dd class="font-mono-feat text-base text-foreground">{{ dash(n.providerType) }}</dd>
              </div>
              <div class="flex items-center justify-between gap-4 py-1.5">
                <dt class="text-base text-muted-foreground">Provider ID</dt>
                <dd class="font-mono-feat text-base text-foreground">{{ dash(n.providerId) }}</dd>
              </div>
              <div class="flex items-start justify-between gap-4 py-1.5">
                <dt class="shrink-0 pt-1 text-base text-muted-foreground">Tags</dt>
                <dd class="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-1.5">
                  @for (t of n.tags; track t.id) {
                    <hlm-badge variant="secondary" class="font-mono-feat">{{ t.name }}</hlm-badge>
                  } @empty {
                    <span class="text-base text-muted-foreground">—</span>
                  }
                </dd>
              </div>
            </dl>
          </div>

          <!-- ===== NUMBER BLOCK (shared across the block) ===== -->
          @if (n.numberBlock; as b) {
            <div class="flex flex-col gap-1">
              <p class="cmt-eyebrow">Number Block</p>
              <dl class="flex flex-col divide-y divide-border/70">
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Block ID</dt>
                  <dd class="font-mono-feat text-base text-foreground">#{{ b.id }}</dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Short form (block)</dt>
                  <dd class="font-mono-feat text-base text-foreground">{{ b.shortForm }}</dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Dialling / Prefix</dt>
                  <dd class="font-mono-feat text-base text-foreground">{{ b.vorwahl }} / {{ b.prefix }}</dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Suffix start / Amount</dt>
                  <dd class="font-mono-feat text-base text-foreground">{{ b.suffixStart }} / {{ b.amount }}</dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Country / City prefix</dt>
                  <dd class="font-mono-feat text-base text-foreground">{{ b.countryPrefix }} / {{ b.cityPrefix }}</dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Carrier</dt>
                  <dd class="font-mono-feat text-base text-foreground">{{ dash(b.carrier) }}</dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Callcenter ID</dt>
                  <dd class="font-mono-feat text-base text-foreground">{{ dash(b.callcenterId) }}</dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Free of charge</dt>
                  <dd class="text-base text-foreground">{{ b.freeOfCharge ? 'yes' : 'no' }}</dd>
                </div>
              </dl>
            </div>
          }
        </div>

        <!-- ===== REGULATION (number-level) ===== -->
        <div class="flex flex-col gap-1">
          <p class="cmt-eyebrow">Regulation</p>
          <dl class="flex flex-col divide-y divide-border/70">
            <div class="flex items-center justify-between gap-4 py-1.5">
              <dt class="text-base text-muted-foreground">Status</dt>
              <dd>
                @if (n.currentRegulationStatus) {
                  <hlm-badge [variant]="regStatusVariant()">{{ humanize(n.currentRegulationStatus) }}</hlm-badge>
                } @else {
                  <!-- null regulation status = no regulation required → "OK" (matches the list's green OK). -->
                  <hlm-badge variant="success">OK</hlm-badge>
                }
              </dd>
            </div>
            @if (n.regulationMessage) {
              <div class="flex items-center justify-between gap-4 py-1.5">
                <dt class="text-base text-muted-foreground">Message</dt>
                <dd class="text-base text-foreground text-right">{{ n.regulationMessage }}</dd>
              </div>
            }
          </dl>

          <!-- Rejection reasons surfaced prominently, mirroring the fix dialog's banner. -->
          @if (n.rejectionReasons?.length) {
            <div class="mt-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
              <div class="flex items-center gap-2 text-sm font-semibold text-destructive">
                <hlm-icon name="lucideCircleX" size="0.9rem" /> Regulation rejected
              </div>
              <ul class="mt-2 list-disc space-y-1 pl-7 text-sm text-destructive">
                @for (reason of n.rejectionReasons; track reason) {
                  <li>{{ reason }}</li>
                }
              </ul>
            </div>
          }

          <!-- Lazy regulation address (failed/rejected numbers only) -->
          @if (canLoadAddress()) {
            @if (regAddr(); as a) {
              <dl class="mt-2 flex flex-col divide-y divide-border/70 rounded-lg border border-border bg-muted/30 p-3">
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Company</dt>
                  <dd class="text-base text-foreground text-right">{{ dash(a.company) }}{{ a.companyVat ? ' · VAT ' + a.companyVat : '' }}</dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Address</dt>
                  <dd class="text-base text-foreground text-right">
                    {{ dash(a.street) }} {{ a.buildingNumber }}{{ a.buildingLetter }}, {{ a.zipCode }} {{ a.city }}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Region / Country</dt>
                  <dd class="text-base text-foreground text-right">{{ dash(a.province || a.state) }} / {{ dash(a.countryCode) }}</dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Name</dt>
                  <dd class="text-base text-foreground text-right">{{ dash(a.firstName) }} {{ a.lastName }}</dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">Born</dt>
                  <dd class="text-base text-foreground text-right">{{ dash(a.dateOfBirth) }} {{ a.placeOfBirth ? '· ' + a.placeOfBirth : '' }}</dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-1.5">
                  <dt class="text-base text-muted-foreground">ID</dt>
                  <dd class="text-base text-foreground text-right">{{ dash(a.idType) }} {{ a.idNumber }}</dd>
                </div>
                @if (a.nationality || a.taxCode) {
                  <div class="flex items-center justify-between gap-4 py-1.5">
                    <dt class="text-base text-muted-foreground">Nationality / Tax code</dt>
                    <dd class="text-base text-foreground text-right">{{ dash(a.nationality) }} / {{ dash(a.taxCode) }}</dd>
                  </div>
                }
              </dl>
            } @else if (regHint(); as hint) {
              <div class="mt-2 flex items-center gap-2 rounded-lg border border-accent/30 bg-accent/10 p-2.5 text-sm text-foreground">
                <hlm-icon name="lucideCircleAlert" size="0.9rem" class="text-accent" />
                <span>{{ hint }}</span>
              </div>
            }

            <!-- Actions on one row; "Fix regulation" is the primary action. -->
            <div class="mt-3 flex flex-wrap items-center gap-2">
              @if (!regAddr() && !regHint()) {
                <button hlmBtn variant="outline" size="sm"
                  (click)="loadAddress()" [disabled]="regLoading()">
                  <hlm-icon name="lucideScale" size="0.9rem" />
                  {{ regLoading() ? 'Loading…' : 'Load regulation address' }}
                </button>
              }
              <!-- Repair the rejected/failed regulation: opens the prefilled fix form (EB-3890).
                   Alert-coloured to match the overview's Fix button. -->
              <button hlmBtn variant="destructive" size="sm" (click)="openFixRegulation()">
                <hlm-icon name="lucideWrench" size="0.9rem" /> Fix regulation
              </button>
            </div>
          }
        </div>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" type="button" (click)="close()">Close</button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class NumberDetailsDialogComponent {
  readonly dialogRef = inject(MatDialogRef<NumberDetailsDialogComponent>);
  readonly data: NumberDetailsDialogData = inject(MAT_DIALOG_DATA);
  private readonly api = inject(CcNumbersService);
  private readonly toast = inject(ToastService);
  private readonly dialog = inject(MatDialog);

  readonly n = this.data.number;
  readonly short = computed(() => shortNotation(this.n));

  readonly regLoading = signal(false);
  readonly regAddr = signal<RegulationAddressDetails | null>(null);
  readonly regHint = signal<string | null>(null);

  /** Set when a Fix-regulation re-submission succeeds; returned on close so the parent reloads the list. */
  readonly changed = signal(false);

  /**
   * The address is only resolvable for failed/rejected numbers (the endpoint 400s otherwise).
   * The numbers list does NOT reliably populate `currentRegulationStatus` (it comes back null even
   * for DB-rejected numbers — verified on Test), but it does populate `rejectionReasons` and
   * `regulationAddressId`. So gate primarily on those, keeping the status check as a fallback.
   */
  readonly canLoadAddress = computed(() => {
    if (this.n.regulationAddressId == null) return false;
    const hasRejections = (this.n.rejectionReasons?.length ?? 0) > 0;
    const status = this.n.currentRegulationStatus;
    const statusFailed = status != null && VOXBONE_ERROR_STATUSES.includes(status);
    return hasRejections || statusFailed;
  });

  dash(v: unknown): string {
    return v === null || v === undefined || v === '' ? '—' : String(v);
  }

  humanize(status: string | null): string {
    return status ? status.replace(/_/g, ' ') : '—';
  }

  /** Badge tone for the regulation status chip: red for problems, amber for pending, green otherwise. */
  regStatusVariant(): 'destructive' | 'accent' | 'success' {
    const s = this.n.currentRegulationStatus;
    if (s && VOXBONE_ERROR_STATUSES.includes(s)) return 'destructive';
    if (s && VOXBONE_PENDING_STATUSES.includes(s)) return 'accent';
    return 'success';
  }

  /** True when this number's regulation is failed/rejected — drives the dialog's alert-coloured header. */
  isRegulationError(): boolean {
    const s = this.n.currentRegulationStatus;
    return s != null && VOXBONE_ERROR_STATUSES.includes(s);
  }

  copy(text: string, event?: Event): void {
    copyToClipboard(this.toast, text, event);
  }

  /** Close the read-only dialog, signalling the parent whether a Fix-regulation re-submission happened. */
  close(): void {
    this.dialogRef.close(this.changed());
  }

  /**
   * Open the "Fix regulation" dialog for this number. On a successful re-submission (afterClosed=true)
   * flag `changed` so the parent reloads the list when this read-only dialog closes
   * (passed via dialogRef.close(this.changed)).
   */
  openFixRegulation(): void {
    const ref = this.dialog.open(FixRegulationDialogComponent, {
      data: {
        phoneServiceId: this.data.phoneServiceId,
        number: this.n.number,
        numberType: this.n.type,
        rejectionReasons: this.n.rejectionReasons,
        // PS-6489: the number's own ONKZ drives the address check in the fix dialog. Without it the
        // check switches itself off, so this second entry point must pass it just like the overview.
        cityPrefix: this.n.numberBlock?.cityPrefix ?? null,
      } as FixRegulationDialogData,
      // Wide (lg) so the requirements-driven auto-fill form grid uses ~4 columns → far less vertical scroll.
      width: '880px',
      maxWidth: '95vw',
      // Anchor near the top instead of vertically centering — tighter top gap for this tall form dialog.
      position: { top: '2rem' },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.changed.set(true);
    });
  }

  loadAddress(): void {
    this.regLoading.set(true);
    this.regHint.set(null);
    this.api.getRegulationAddress(this.data.phoneServiceId, this.n.number).subscribe({
      next: (addr) => {
        this.regLoading.set(false);
        if (addr) {
          this.regAddr.set(addr);
        } else {
          this.regHint.set('No regulation address stored for this number.');
        }
      },
      error: (err) => {
        this.regLoading.set(false);
        // 400 = the number's verification has not failed (the endpoint's documented guard);
        // anything else (e.g. 500 when the Voxbone DID can no longer be resolved) is a generic failure.
        this.regHint.set(
          err?.status === 400
            ? 'Regulation address can only be loaded for failed/rejected verifications.'
            : 'Could not load the regulation address.',
        );
      },
    });
  }
}
